import { useCallback, useState } from "react";
import { CreateProductRequest, Product } from "../../../types";
import { productService } from "../../../services/productService";
import { errorText, Notify } from "../../orders/quickEdit/useQuickEditSave";

export type ProductPatch = Partial<CreateProductRequest>;

/** Resolves to null when saved, else the error to show next to the field. */
export type SavePatch = (patch: ProductPatch, successMessage: string) => Promise<string | null>;

/** Like SavePatch, for saves that aren't a field patch (e.g. combo prices). */
export type RunSave = (request: () => Promise<unknown>, successMessage: string) => Promise<string | null>;

/**
 * Saving for the product drawer's quick edits: sends just the changed
 * fields, re-fetches the full product and hands it to the screen (drawer
 * and table row refresh from it), then shows a toast. Each editor keeps
 * its own error, so a failed price doesn't show up under the barcode.
 */
export function useProductQuickSave(
    productId: string,
    onProductChanged: (product: Product) => void,
    notify: Notify,
) {
    const [saving, setSaving] = useState(false);

    const run: RunSave = useCallback(
        async (request, successMessage) => {
            setSaving(true);
            try {
                await request();
            } catch (err) {
                setSaving(false);
                return errorText(err);
            }
            try {
                onProductChanged(await productService.getProductById(productId));
            } catch {
                // Saved; reopening the product shows it.
            }
            setSaving(false);
            notify(successMessage);
            return null;
        },
        [productId, onProductChanged, notify],
    );

    const save: SavePatch = useCallback(
        (patch, successMessage) => run(() => productService.updateProduct(productId, patch), successMessage),
        [run, productId],
    );

    return { saving, save, run };
}
